import React, {
  forwardRef,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
  useState,
} from 'react';
import merge from 'deepmerge';
import { Template } from 'devextreme-react';
import { NuvoForm } from '@nuvolo/nuux-core/components/NuvoForm';
import mapFieldItems, { flattenMappedFieldItems } from './mapFieldItems';
import type { FormProps, FormRefType } from './FormProps';
import renderItems from './renderItems';
import Styles from './Styles';
import dot from './templates/dot';
import dxFormComponent from 'devextreme-react/form';
import TenantSelector from '../TenantSelector';
import { Tenant } from '#/graphql/types';
import _, { get } from 'lodash';
import useSearchQuery from '#/common/hooks/useSearchQuery';
import { clearFilters } from '#/common/utils/clearFilters';
import { uuidPattern } from '#/common/utils/patterns/uuid';
import WorkingOnItThrobber from '#/app/components/Throbbers/WorkingOnItThrobber';
import deepMapChildren from '#/common/utils/deepMapChildren';
import { PageActions } from '../SubPage';
import mapPageActions from './mapPageActions';
import { useSearchParams, useBlocker } from 'react-router-dom';
import type { BlockerFunction } from 'react-router-dom';
import useToast from '#/common/hooks/useToast';
import { setFormDirty, isFormDirty as isModuleFormDirty } from './dirtyFormTracker';

const NuvoFormWrapper = forwardRef<dxFormComponent, React.ComponentProps<typeof NuvoForm>>(
  (props, ref) => {
    return <NuvoForm {...(props as any)} ref={ref} />;
  },
);

export const Throbber = () => (
  <>
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        background: 'grey',
        width: '100%',
        height: '100%',
        opacity: 0.5,
        zIndex: 1000,
        overflow: 'hidden',
        touchAction: 'none',
        overscrollBehavior: 'contain',
      }}></div>
    <div
      style={{
        position: 'absolute',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        top: '50vh',
        zIndex: '9999',
      }}>
      <WorkingOnItThrobber />
    </div>
  </>
);

const normalize = (data: unknown): unknown => {
  if (data === null || data === undefined || data === '') return undefined;
  if (Array.isArray(data)) return data.map(normalize);
  if (typeof data === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      const n = normalize(v);
      if (n !== undefined) result[k] = n;
    }
    return result;
  }
  return data;
};

const Form = <T,>(
  {
    record,
    readonly,
    fields = [],
    actions,
    supportTenant,
    validationGroup = 'FormValidationGroup',
    onInitialized,
    onContentReady,
    onFieldDataChanged,
    children,
  }: FormProps<T>,
  ref: React.ForwardedRef<FormRefType>,
) => {
  const [searchParams] = useSearchParams();
  const formRef = useRef<dxFormComponent>(null);
  const prevIdRef = useRef<unknown>(Symbol('uninitialized'));
  const baselineRef = useRef<Record<string, unknown>>({});
  const isDirtyRef = useRef(false);
  // Tracks which individual fields differ from the baseline.
  // When the set is empty the form is clean again.
  const dirtyFieldsRef = useRef<Set<string>>(new Set());
  const suppressChangeRef = useRef(false);
  // DevExtreme fires onFieldDataChanged during initialization — ignore those.
  const isInitializedRef = useRef(false);
  // Prevents the blocker effect from running twice when React Router re-creates the
  // blocker object reference while still in 'blocked' state (caused by its internal go(-delta)).
  const blockerHandlingRef = useRef(false);
  // Prevents our own go(1) restoration call from re-triggering the popstate handler.
  const isRestoringRef = useRef(false);
  // Prevents concurrent confirm dialogs when the browser back button is pressed rapidly.
  const popstateHandlingRef = useRef(false);

  // useLayoutEffect always runs (unlike useImperativeHandle which only runs when ref is non-null).
  // This ensures clearDirty / clearFieldDirty are available on the DX instance for all forms,
  // including those that don't forward a ref to <Form> (e.g. CapitalNeedForm).
  useLayoutEffect(() => {
    const instance = formRef?.current?.instance as FormRefType | undefined;
    if (!instance) return;

    Object.defineProperty(instance, 'isDirty', {
      get: () => isDirtyRef.current,
      configurable: true,
    });
    instance.clearDirty = () => {
      dirtyFieldsRef.current = new Set();
      isDirtyRef.current = false;
      // Advance the baseline to the current form values so that any subsequent
      // DevExtreme field-change events (e.g. triggered by an Apollo cache update
      // re-rendering fieldItems) don't re-dirty the form after a successful save.
      const currentData = formRef.current?.instance.option('formData');
      if (currentData) {
        baselineRef.current = _.cloneDeep(currentData) as Record<string, unknown>;
      }
      setFormDirty(false);
    };
    instance.clearFieldDirty = (field: string) => {
      dirtyFieldsRef.current.delete(field);
      const dirty = dirtyFieldsRef.current.size > 0;
      isDirtyRef.current = dirty;
      setFormDirty(dirty);
    };

    // Forward the enriched instance to the parent ref (replicates useImperativeHandle behaviour).
    if (typeof ref === 'function') {
      ref(instance);
      return () => ref(null);
    } else if (ref) {
      (ref as React.MutableRefObject<FormRefType>).current = instance;
      return () => {
        (ref as React.MutableRefObject<FormRefType | null>).current = null;
      };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const id = get(record, 'id');

  const readonlyRef = useRef(readonly);
  readonlyRef.current = readonly;

  const [isLoading, setIsLoading] = useState(false);
  const setLoading = useCallback((isLoading: boolean) => {
    setIsLoading(isLoading);
    formRef.current?.instance.option('disabled', isLoading);
    formRef.current?.instance.option('readOnly', isLoading || readonlyRef.current);
    formRef.current?.instance.repaint();
  }, []);

  const query = useSearchQuery();

  const fieldItems = useMemo(() => {
    const items = mapFieldItems(fields);
    const currentFormData = formRef.current?.instance.option('formData');
    const newItems = deepMapChildren(items, (item) => {
      const valueSource =
        currentFormData && typeof currentFormData === 'object' ? currentFormData : record;
      const val = get(valueSource, item.dataField);
      if (item.dataField && val === null) {
        item.editorOptions = item.editorOptions ?? {};
        item.editorOptions.value = undefined;
      }
      return item;
    });
    return newItems;
  }, [fields, record]);

  // Use local state for formData to handle field value changes
  const [formData] = useState(() => {
    const fieldItemsUngrouped = flattenMappedFieldItems(fieldItems);
    const recordProperties = fieldItemsUngrouped.map((fieldItem) => ({
      dataField: fieldItem.dataField,
      dataType: fieldItem.editorOptions?.type || 'string',
    }));

    const { filtersClean } = clearFilters(query.filters, recordProperties);
    // Filters are arrays. If there are no filter elements, then there must only be one filter
    // and it is the whole filtersClean object
    // (we get ['tenantId', '=', ${id}], not [['tenantId', '=', ${id}]] unlike when there are multiple filters)
    const filtersFormatted = filtersClean.some((el) => Array.isArray(el))
      ? filtersClean
      : [filtersClean];
    // AND string or any other non-array doesn't interest us. Same goes for filters that
    // say anything other than [${varName}, '=', ${something}]
    const filtersToUse = filtersFormatted.filter(
      (filter) => Array.isArray(filter) && filter[1] === '=',
    );

    const keyValuePairsFromQuery = filtersToUse.map(([propertyName, _, propertyValue]) => [
      propertyName,
      propertyValue,
    ]);

    const queryValuesAsObj = keyValuePairsFromQuery.reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {} as Partial<typeof record>,
    );

    // URL overrides record
    return merge<T>({ ...record }, queryValuesAsObj);
  });

  // Prioritize tenantId from URL over formData
  const tenantIdFromUrl = searchParams.get('tenantId') ?? undefined;
  const tenantIdFromForm = get(formData, 'tenantId') as string | undefined;
  const tenantId = tenantIdFromUrl || tenantIdFromForm;

  // When the id changes reset dirty state so a freshly loaded record isn't considered dirty.
  if (prevIdRef.current !== id) {
    prevIdRef.current = id;
    baselineRef.current = _.cloneDeep(formData) as Record<string, unknown>;
    dirtyFieldsRef.current = new Set();
    isDirtyRef.current = false;
    isInitializedRef.current = false;
    setFormDirty(false);
  }

  // ESSENTIAL: hydrate tenantId from URL into DevExtreme's internal state so submit uses the correct value.
  useEffect(() => {
    if (!id && supportTenant && tenantIdFromUrl && !tenantIdFromForm) {
      suppressChangeRef.current = true;
      formRef.current?.instance.updateData('tenantId', tenantIdFromUrl);
      suppressChangeRef.current = false;
    }
  }, [id, supportTenant, tenantIdFromUrl, tenantIdFromForm]);

  // Helper function to check if navigation is from "new" form to edit form (successful creation)
  const isSuccessfulCreation = useCallback((currentPath: string, nextPath: string) => {
    // Handle different "new" form patterns:
    // 1. /module/new -> /module/uuid/details
    // 2. /module/new/details -> /module/uuid/details

    let basePath = '';

    if (currentPath.endsWith('/new/details')) {
      // Pattern: /module/new/details -> /module/uuid/details
      basePath = currentPath.replace('/new/details', '');
    } else if (currentPath.endsWith('/new')) {
      // Pattern: /module/new -> /module/uuid/details
      basePath = currentPath.replace('/new', '');
    } else {
      // Not a "new" form
      return false;
    }

    // Escape special regex characters in the base path
    const escapedBasePath = basePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create pattern to match: /basePath/uuid or /basePath/uuid/details

    const successPattern = new RegExp(`^${escapedBasePath}/${uuidPattern}(?:/details)?$`, 'i');

    return successPattern.test(nextPath);
  }, []);

  const { confirm } = useToast();

  // Only block PUSH/REPLACE (sidebar, link clicks) — POP is handled by the popstate listener.
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation, historyAction }) => {
      if (historyAction === 'POP') return false;
      if (readonly || !isDirtyRef.current || !isModuleFormDirty()) return false;
      if (currentLocation.pathname === nextLocation.pathname) return false;
      if (isSuccessfulCreation(currentLocation.pathname, nextLocation.pathname)) return false;
      return true;
    },
    [readonly, isSuccessfulCreation],
  );

  const blocker = useBlocker(shouldBlock);

  // Browser back/forward button — handled with a capturing popstate listener so it fires
  // BEFORE React Router's own listener, giving us a chance to call go(1) before the router
  // processes the navigation and unmounts the form.
  useEffect(() => {
    if (readonly) return;
    const handlePopState = async () => {
      // Ignore the popstate fired by our own go(1) restoration.
      if (isRestoringRef.current) {
        isRestoringRef.current = false;
        return;
      }
      if (!isDirtyRef.current || !isModuleFormDirty()) return;
      // Guard against concurrent confirm dialogs (e.g. rapid back-button presses).
      if (popstateHandlingRef.current) return;
      popstateHandlingRef.current = true;
      // Undo the back navigation so we can show the modal while still on this page.
      isRestoringRef.current = true;
      window.history.go(1);
      try {
        const leave = await confirm('warning', 'Leave page? Changes you made may not be saved.', [
          'Cancel',
          'Leave',
        ]);
        if (leave) {
          dirtyFieldsRef.current = new Set();
          isDirtyRef.current = false;
          setFormDirty(false);
          // Navigate back now that dirty state is cleared.
          window.history.go(-1);
        }
      } catch {
        // Stay on the page if the dialog is dismissed unexpectedly.
      } finally {
        popstateHandlingRef.current = false;
      }
    };
    window.addEventListener('popstate', handlePopState, true); // capture: true — fires before React Router
    return () => window.removeEventListener('popstate', handlePopState, true);
  }, [readonly, confirm]);

  // PUSH / REPLACE (sidebar, link clicks): show confirmation toast.
  // Guard prevents double-firing when React Router re-creates the blocker reference.
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      blockerHandlingRef.current = false;
      return;
    }
    if (blockerHandlingRef.current) return;
    blockerHandlingRef.current = true;
    (async () => {
      try {
        const leave = await confirm('warning', 'Leave page? Changes you made may not be saved.', [
          'Cancel',
          'Leave',
        ]);
        if (leave) {
          dirtyFieldsRef.current = new Set();
          isDirtyRef.current = false;
          setFormDirty(false);
          blocker.proceed();
        } else {
          blocker.reset?.();
        }
      } catch {
        blocker.reset?.();
      } finally {
        blockerHandlingRef.current = false;
      }
    })();
  }, [blocker, confirm]);

  // Browser close / refresh.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current && isModuleFormDirty() && !readonly) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [readonly]);

  const selectTenant = useCallback((tenant: Tenant) => {
    suppressChangeRef.current = true;
    formRef.current?.instance.updateData('tenantId', tenant.id);
    suppressChangeRef.current = false;
    formRef.current?.instance.repaint();
  }, []);

  // Dirty tracking: only active after DevExtreme finishes initialization.
  // Track per-field dirtiness using e.dataField + e.value (reliable) rather than
  // option('formData') (unreliable). Form is clean when no fields differ from baseline.
  const handleFieldDataChanged = useCallback(
    (
      e: Parameters<NonNullable<React.ComponentProps<typeof NuvoForm>['onFieldDataChanged']>>[0],
    ) => {
      if (!suppressChangeRef.current && isInitializedRef.current) {
        const field = e.dataField;
        if (field) {
          const baselineValue = get(baselineRef.current, field);
          if (_.isEqual(normalize(e.value), normalize(baselineValue))) {
            dirtyFieldsRef.current.delete(field);
          } else {
            dirtyFieldsRef.current.add(field);
          }
          const dirty = dirtyFieldsRef.current.size > 0;
          isDirtyRef.current = dirty;
          setFormDirty(dirty);
        }
      }
      onFieldDataChanged?.(e);
    },
    [onFieldDataChanged],
  );

  const handleInitialized = useCallback(
    (e: Parameters<NonNullable<React.ComponentProps<typeof NuvoForm>['onInitialized']>>[0]) => {
      isInitializedRef.current = true;
      onInitialized?.(e);
    },
    [onInitialized],
  );

  // Supplementary dirty detection for string-type fields: DevExtreme only fires
  // onFieldDataChanged when a text input loses focus, so if the user is still
  // actively typing and presses Back or Refresh, isDirtyRef would be false.
  // Listening to native 'input' events catches uncommitted keystrokes immediately.
  const handleDomInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!isInitializedRef.current || suppressChangeRef.current) return;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      if (!isDirtyRef.current) {
        isDirtyRef.current = true;
        setFormDirty(true);
      }
    }
  }, []);

  const pageActions = useMemo(
    () => mapPageActions(actions, setLoading, formRef, fields),
    [actions, setLoading, fields],
  );

  return (
    <Styles>
      {isLoading && <Throbber />}
      {!id && supportTenant && !tenantId && <TenantSelector onTenantSelect={selectTenant} />}
      {pageActions && <PageActions actions={pageActions} />}
      <div onInput={handleDomInput}>
        <NuvoFormWrapper
          validationGroup={validationGroup}
          ref={formRef}
          formData={formData}
          readOnly={readonly}
          onInitialized={handleInitialized}
          onFieldDataChanged={handleFieldDataChanged}
          onContentReady={onContentReady}>
          {renderItems(fieldItems)}
          <Template name="dot" render={dot} />

          {children}
        </NuvoFormWrapper>
      </div>
    </Styles>
  );
};

export default React.memo(forwardRef(Form)) as typeof Form;
